<template>
  <div class="sprintly-input-wrapper">
    <label v-if="label" class="input-label">
      {{ label }}
      <span v-if="required" class="required-mark">*</span>
    </label>
    
    <div class="input-container" :class="{ 'has-error': error, 'is-focused': isFocused }">
      <v-icon v-if="prependIcon" class="input-prepend-icon" size="18">
        {{ prependIcon }}
      </v-icon>
      
      <input
        v-if="type !== 'textarea'"
        :type="computedType"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        class="input-field"
        @input="$emit('input', $event.target.value)"
        @focus="onFocus"
        @blur="onBlur"
        @keyup.enter="$emit('enter')"
      />
      
      <textarea
        v-else
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :rows="rows"
        class="input-field textarea-field"
        @input="$emit('input', $event.target.value)"
        @focus="onFocus"
        @blur="onBlur"
      />
      
      <button
        v-if="type === 'password'"
        type="button"
        class="toggle-password-btn"
        @click="togglePasswordVisibility"
      >
        <v-icon size="18">
          {{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}
        </v-icon>
      </button>
      
      <v-icon v-if="appendIcon" class="input-append-icon" size="18">
        {{ appendIcon }}
      </v-icon>
    </div>
    
    <div v-if="error || hint" class="input-message">
      <span v-if="error" class="error-text">
        <v-icon size="14" color="#e67e4e">mdi-alert-circle</v-icon>
        {{ error }}
      </span>
      <span v-else-if="hint" class="hint-text">{{ hint }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SprintlyInput',
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'password', 'email', 'number', 'textarea'].includes(value)
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    prependIcon: {
      type: String,
      default: null
    },
    appendIcon: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    hint: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    rows: {
      type: Number,
      default: 4
    }
  },
  data() {
    return {
      isFocused: false,
      showPassword: false
    }
  },
  computed: {
    computedType() {
      if (this.type === 'password') {
        return this.showPassword ? 'text' : 'password';
      }
      return this.type;
    }
  },
  methods: {
    onFocus() {
      this.isFocused = true;
      this.$emit('focus');
    },
    onBlur() {
      this.isFocused = false;
      this.$emit('blur');
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    }
  }
}
</script>

<style lang="scss" scoped>
/* === WRAPPER === */
.sprintly-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

/* === LABEL === */
.input-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.3s ease;
}

.theme--light .input-label {
  color: #1a1a1a;
}

.required-mark {
  color: #e67e4e;
}

/* === CONTAINER === */
.input-container {
  display: flex;
  align-items: center;
  background: #000000;
  border: none;
  border-radius: 8px;
  padding: 0 14px;
  transition: all 0.3s ease;
  gap: 10px;

  &.is-focused {
    border-color: #BC3C44;
    box-shadow: 0 0 0 3px rgba(188, 60, 68, 0.1);
  }

  &.has-error {
    border-color: #e67e4e;
  }

  &:hover:not(.has-error):not(.is-focused) {
    border-color: #3a3e45;
  }
}

.theme--light .input-container {
  background: #f9fafb;
  border-color: #d1d5db;

  &.is-focused {
    background: #ffffff;
    border-color: #BC3C44;
  }

  &:hover:not(.has-error):not(.is-focused) {
    border-color: #b0b0b0;
  }
}

/* === FIELD === */
.input-field {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 0.9rem;
  padding: 12px 0;
  font-family: 'Inter', sans-serif;

  &::placeholder {
    color: #5a5f66;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.textarea-field {
    resize: vertical;
    min-height: 80px;
    padding: 12px 0;
    line-height: 1.5;
  }
}

.theme--light .input-field {
  color: #1a1a1a;

  &::placeholder {
    color: #9ca3af;
  }
}

/* === ICONS === */
.input-prepend-icon,
.input-append-icon {
  color: #999999;
  flex-shrink: 0;
}

.theme--light .input-prepend-icon,
.theme--light .input-append-icon {
  color: #666666;
}

.toggle-password-btn {
  background: transparent;
  border: none;
  color: #999999;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.3s;

  &:hover {
    background: #2a2a2a;
    color: #ffffff;
  }
}

.theme--light .toggle-password-btn {
  color: #666666;

  &:hover {
    background: #e0e0e0;
    color: #1a1a1a;
  }
}

/* === MESSAGES === */
.input-message {
  min-height: 18px;
  font-size: 0.8rem;
}

.error-text {
  color: #e67e4e;
  display: flex;
  align-items: center;
  gap: 4px;
}

.hint-text {
  color: #999999;
  transition: color 0.3s ease;
}

.theme--light .hint-text {
  color: #666666;
}

/* === RESPONSIVE === */
@media (max-width: 600px) {
  .input-field {
    font-size: 0.85rem;
    padding: 10px 0;
  }

  .input-container {
    padding: 0 12px;
  }
}
</style>
